import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + "/api/procurement";

const Tags = () => {
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const fetchTags = async () => {
    try {
      const response = await fetch(`${API}/tags`);
      const data = await response.json();
      setTags(data);
    } catch (error) {
      console.error('Error fetching tags:', error);
      toast.error('خطأ في تحميل التصنيفات');
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingTag 
        ? `${API}/tags/${editingTag.id}`
        : `${API}/tags`;
      
      const method = editingTag ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchTags();
        setShowDialog(false);
        setEditingTag(null);
        setFormData({ name: '', description: '' });
        toast.success(editingTag ? 'تم تحديث التصنيف بنجاح' : 'تم إضافة التصنيف بنجاح');
      } else {
        toast.error('خطأ في حفظ التصنيف');
      }
    } catch (error) {
      console.error('Error saving tag:', error);
      toast.error('خطأ في حفظ التصنيف');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا التصنيف؟')) {
      try {
        const response = await fetch(`${API}/tags/${id}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          fetchTags();
          toast.success('تم حذف التصنيف بنجاح');
        }
      } catch (error) {
        console.error('Error deleting tag:', error);
        toast.error('خطأ في حذف التصنيف');
      }
    }
  };

  const handleEdit = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      description: tag.description || ''
    });
    setShowDialog(true);
  };

  const handleAdd = () => {
    setEditingTag(null);
    setFormData({ name: '', description: '' });
    setShowDialog(true);
  };

  return (
    <div className="p-6" dir="rtl">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/procurement')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition"
          >
            <ArrowLeft size={24} />
            <span>العودة</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-800">إدارة التصنيفات</h1>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          <Plus size={20} />
          <span>إضافة تصنيف</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">الاسم</th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">الوصف</th>
              <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {tags.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-6 py-8 text-center text-gray-500">
                  لا توجد تصنيفات. قم بإضافة تصنيف جديد.
                </td>
              </tr>
            ) : (
              tags.map((tag) => (
                <tr key={tag.id} className="border-t hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-800 font-medium">{tag.name}</td>
                  <td className="px-6 py-4 text-gray-600">{tag.description || '-'}</td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleEdit(tag)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
                        title="تعديل"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(tag.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                        title="حذف"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" dir="rtl">
            <h2 className="text-xl font-bold mb-4 text-gray-800">
              {editingTag ? 'تعديل التصنيف' : 'إضافة تصنيف جديد'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الاسم *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="مثال: عرض مقبول، قيد المراجعة، ..."
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الوصف (اختياري)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="وصف مختصر للتصنيف"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowDialog(false);
                    setEditingTag(null);
                    setFormData({ name: '', description: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                >
                  {editingTag ? 'تحديث' : 'إضافة'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tags;
